import { ChartNoAxesColumn } from "lucide-react";
import { useGetList, useTranslate } from "ra-core";
import { useMemo } from "react";
import { Link } from "react-router";
import { Card } from "@/components/ui/card";

import { getOpenDealStages } from "../deals/dealUtils";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Deal } from "../types";
import { useDashboardScope } from "./useDashboardScope";

/**
 * Ceiling on the fetched window. Every bar is summed in the browser from this
 * one request; past this many open deals the widget says the bars are partial
 * rather than under-reporting silently, and the sums belong server-side.
 */
const MAX_FETCHED_DEALS = 500;

/** Link to the deals board narrowed down to a single stage. */
const dealsLinkForStage = (stage: string) => ({
  pathname: "/deals",
  search: `filter=${encodeURIComponent(JSON.stringify({ stage }))}`,
});

/**
 * One bar per open deal stage: how many deals sit there and what they are
 * worth. Stages listed in `dealPipelineStatuses` are closed outcomes and are
 * left out — a pipeline is what is still in play.
 */
export const PipelineByStage = () => {
  const translate = useTranslate();
  const { dealPipelineStatuses, dealStages } = useConfigurationContext();
  const { salesId, isTeam, isPending: isScopePending } = useDashboardScope();

  const openStages = useMemo(
    () => getOpenDealStages(dealStages, dealPipelineStatuses),
    [dealPipelineStatuses, dealStages],
  );

  // Closed deals are excluded server-side: none of them is ever drawn, so
  // letting them fill the window would only shrink the bars this widget can
  // actually show.
  const {
    data: deals,
    total,
    isPending,
  } = useGetList<Deal>(
    "deals",
    {
      pagination: { page: 1, perPage: MAX_FETCHED_DEALS },
      sort: { field: "amount", order: "DESC" },
      filter: {
        "archived_at@is": null,
        "stage@in": `(${openStages.map((stage) => stage.value).join(",")})`,
        ...(isTeam ? {} : { sales_id: salesId }),
      },
    },
    { enabled: !isScopePending && openStages.length > 0 },
  );

  const stageRows = useMemo(
    () =>
      openStages.map((stage) => {
        const stageDeals = (deals ?? []).filter(
          (deal) => deal.stage === stage.value,
        );
        return {
          value: stage.value,
          label: stage.label,
          count: stageDeals.length,
        };
      }),
    [deals, openStages],
  );

  const isPartial = (total ?? 0) > MAX_FETCHED_DEALS;

  const maxCount = stageRows.reduce(
    (largest, row) => Math.max(largest, row.count),
    0,
  );
  const hasOpenDeals = stageRows.some((row) => row.count > 0);

  if (isPending) return null;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center">
        <div className="mr-3 flex">
          <ChartNoAxesColumn className="text-muted-foreground w-6 h-6" />
        </div>
        <h2 className="text-xl font-semibold text-muted-foreground flex-1">
          {translate("crm.dashboard.pipeline.title")}
        </h2>
      </div>
      <Card className="p-4 gap-3">
        {!hasOpenDeals ? (
          <p className="text-sm text-muted-foreground">
            {translate("crm.dashboard.pipeline.empty")}
          </p>
        ) : (
          stageRows.map((row) => (
            <Link
              key={row.value}
              to={dealsLinkForStage(row.value)}
              className="group flex flex-col gap-1"
            >
              <div className="flex items-baseline justify-between gap-2 text-sm">
                <span className="truncate group-hover:underline">
                  {row.label}
                </span>
                <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                  {row.count}
                </span>
              </div>
              {/*
                The bar restates the count already written beside the label, so
                it carries no information of its own and stays out of the
                accessibility tree.
              */}
              <div className="h-1.5 rounded-full bg-muted" aria-hidden="true">
                <div
                  className="h-full rounded-full bg-primary"
                  style={{
                    width:
                      maxCount > 0 ? `${(row.count / maxCount) * 100}%` : "0%",
                  }}
                />
              </div>
            </Link>
          ))
        )}
        {/*
          The window is capped, so say so rather than presenting a partial sum
          as the whole pipeline. Silent truncation reads as "this is all of it".
        */}
        {isPartial && (
          <p className="text-xs text-muted-foreground">
            {translate("crm.dashboard.pipeline.partial", {
              count: MAX_FETCHED_DEALS,
            })}
          </p>
        )}
      </Card>
    </div>
  );
};
